import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Plus, Edit, Trash2, X, Info } from "lucide-react";
import Swal from "sweetalert2";
import { API_BASE_URL } from "../../../../../api";
import "../parametresStyles/ParamConges.css";

const Toggle = ({ checked, onChange, disabled }) => (
    <button
        type="button"
        className={`pcToggle ${checked ? "is-on" : ""}`}
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
        aria-pressed={checked}
    >
        <span className="pcToggle__thumb" />
    </button>
);

const ParamMissions = () => {
    const [showNatureModal, setShowNatureModal] = useState(false);
    const [editNature, setEditNature] = useState(null);
    const [natureForm, setNatureForm] = useState({});
    const [natureMissions, setNatureMissions] = useState([]);
    const [natureLoading, setNatureLoading] = useState(false);
    const [natureError, setNatureError] = useState(null);
    const [natureErrors, setNatureErrors] = useState({});

    const getNatureHeaders = useCallback(() => ({
        uid: sessionStorage.getItem("uid"),
        Authorization: `Bearer ${sessionStorage.getItem("token")}`,
        "X-Tenant-Id": sessionStorage.getItem("selectedCompanyId"),
        "Content-Type": "application/json",
        Accept: "application/json",
    }), []);

    const fetchNatureMissions = useCallback(async () => {
        setNatureLoading(true);
        setNatureError(null);

        try {
            const resp = await axios.get(`${API_BASE_URL}/api/nature-missions`, {
                headers: getNatureHeaders(),
            });
            setNatureMissions(resp.data || []);
        } catch (err) {
            setNatureError(err?.response?.data?.message || err?.message || "Erreur chargement natures de mission");
        } finally {
            setNatureLoading(false);
        }
    }, [getNatureHeaders]);

    useEffect(() => {
        fetchNatureMissions();
    }, [fetchNatureMissions]);

    const openNatureModal = (nature = null) => {
        setNatureErrors({});
        setEditNature(nature);
        setNatureForm(nature ? {
            code: nature.code || "",
            libelle: nature.libelle || "",
            description: nature.description || "",
            actif: nature.actif ?? true,
        } : {
            code: "",
            libelle: "",
            description: "",
            actif: true,
        });
        setShowNatureModal(true);
    };

    const closeNatureModal = () => {
        setShowNatureModal(false);
        setEditNature(null);
        setNatureForm({});
        setNatureErrors({});
    };

    const validateNatureForm = () => {
        const errs = {};
        if (!natureForm.code?.trim()) errs.code = "Requis";
        if (!natureForm.libelle?.trim()) errs.libelle = "Requis";
        setNatureErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleNatureSubmit = async (e) => {
        e.preventDefault();
        if (!validateNatureForm()) return;

        setNatureLoading(true);
        setNatureError(null);

        const payload = {
            code: natureForm.code?.trim().toUpperCase(),
            libelle: natureForm.libelle?.trim(),
            description: natureForm.description?.trim() || null,
            actif: natureForm.actif ?? true,
        };

        try {
            if (editNature) {
                await axios.put(`${API_BASE_URL}/api/nature-missions/${editNature.id}`, payload, {
                    headers: getNatureHeaders(),
                });
                Swal.fire({ icon: "success", title: "", text: "Nature de mission mise a jour", timer: 1400, showConfirmButton: false });
            } else {
                await axios.post(`${API_BASE_URL}/api/nature-missions`, payload, {
                    headers: getNatureHeaders(),
                });
                Swal.fire({ icon: "success", title: "", text: "Nature de mission ajoutee", timer: 1400, showConfirmButton: false });
            }
            closeNatureModal();
            fetchNatureMissions();
        } catch (err) {
            const message = err?.response?.data?.message || err?.response?.data?.error || err?.message || "Erreur enregistrement nature de mission";
            setNatureError(message);
            Swal.fire({ icon: "error", title: "Erreur", text: message, confirmButtonText: "OK", confirmButtonColor: "#d33" });
        } finally {
            setNatureLoading(false);
        }
    };

    const handleNatureDelete = (nature) => {
        Swal.fire({
            title: "Confirmation",
            text: `Voulez-vous vraiment supprimer la nature "${nature.libelle}" ?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#ee645f",
            cancelButtonColor: "#204385",
            confirmButtonText: "Oui, supprimer",
            cancelButtonText: "Annuler",
        }).then(async (result) => {
            if (!result.isConfirmed) return;

            setNatureLoading(true);
            try {
                await axios.delete(`${API_BASE_URL}/api/nature-missions/${nature.id}`, {
                    headers: getNatureHeaders(),
                });
                setNatureMissions((items) => items.filter((item) => item.id !== nature.id));
                Swal.fire({ icon: "success", title: "", text: "Nature de mission supprimee", timer: 1400, showConfirmButton: false });
            } catch (err) {
                const message = err?.response?.data?.message || err?.response?.data?.error || err?.message || "Erreur suppression nature de mission";
                setNatureError(message);
                Swal.fire({ icon: "error", title: "Erreur", text: message, confirmButtonText: "OK", confirmButtonColor: "#d33" });
            } finally {
                setNatureLoading(false);
            }
        });
    };

    const toggleNatureActive = async (nature) => {
        const payload = {
            code: nature.code,
            libelle: nature.libelle,
            description: nature.description,
            actif: !nature.actif,
        };

        setNatureLoading(true);
        try {
            const resp = await axios.put(`${API_BASE_URL}/api/nature-missions/${nature.id}`, payload, {
                headers: getNatureHeaders(),
            });
            setNatureMissions((items) => items.map((item) => item.id === nature.id ? resp.data : item));
        } catch (err) {
            setNatureError(err?.response?.data?.message || err?.message || "Erreur mise a jour statut");
        } finally {
            setNatureLoading(false);
        }
    };

    const renderNatureModal = () => {
        if (!showNatureModal) return null;
        const isEdit = !!editNature;

        return (
            <div className="pcModal">
                <div className="pcModal__panel" role="dialog" aria-modal="true" style={{ maxWidth: 520 }}>
                    <div className="pcModal__header">
                        <div className="pcModal__title">
                            {isEdit ? "Modifier une nature de mission" : "Ajouter une nature de mission"}
                        </div>
                        <button className="pcIconBtn" onClick={closeNatureModal} type="button" aria-label="Fermer" disabled={natureLoading}>
                            <X size={18} />
                        </button>
                    </div>

                    <div className="pcModal__body">
                        <div className="pcWizGrid">
                            <div className="pcWizField">
                                <label>Code <span className="pcReq">*</span></label>
                                <input
                                    type="text"
                                    className={`pcInput ${natureErrors.code ? "is-error" : ""}`}
                                    value={natureForm.code || ""}
                                    onChange={(e) => setNatureForm((p) => ({ ...p, code: e.target.value.toUpperCase() }))}
                                    placeholder="EVENT"
                                />
                                {natureErrors.code && <span className="pcErrMsg">{natureErrors.code}</span>}
                            </div>

                            <div className="pcWizField">
                                <label>Libelle <span className="pcReq">*</span></label>
                                <input
                                    type="text"
                                    className={`pcInput ${natureErrors.libelle ? "is-error" : ""}`}
                                    value={natureForm.libelle || ""}
                                    onChange={(e) => setNatureForm((p) => ({ ...p, libelle: e.target.value }))}
                                    placeholder="Evenement"
                                />
                                {natureErrors.libelle && <span className="pcErrMsg">{natureErrors.libelle}</span>}
                            </div>

                            <div className="pcWizField is-full">
                                <label>Description</label>
                                <textarea
                                    className="pcInput pcInput--textarea"
                                    rows={3}
                                    value={natureForm.description || ""}
                                    onChange={(e) => setNatureForm((p) => ({ ...p, description: e.target.value }))}
                                    placeholder="Ex: Evenement, formation, reunion client..."
                                />
                            </div>

                            <div className="pcWizField is-full">
                                <div className="pcInfoBox">
                                    <Toggle checked={!!natureForm.actif} onChange={(v) => setNatureForm((p) => ({ ...p, actif: v }))} disabled={natureLoading} />
                                    <div>
                                        <div style={{ fontWeight: 600, fontSize: 12 }}>Nature active</div>
                                        <div style={{ fontSize: 11, color: "#64748b" }}>Disponible dans les demandes de mission.</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="pcModal__footer">
                        <button className="pcBtn pcBtn--ghost" type="button" onClick={closeNatureModal} disabled={natureLoading}>Annuler</button>
                        <button className="pcBtn pcBtn--primary" type="button" onClick={handleNatureSubmit} disabled={natureLoading}>{isEdit ? "Enregistrer" : "Créer"}</button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="pcPage">
            <div className="pcHeader">
                <div>
                    <h1>Gestion des natures de missions</h1>
                    {/*<p>Gerez uniquement les natures de mission. Les ordres de mission sont saisis directement dans les demandes.</p>*/}
                    {natureError && <div style={{ marginTop: 8, color: "#b91c1c", fontWeight: 700 }}>Erreur: {natureError}</div>}
                </div>
            </div>

            <div className="pcWrap">
                <div className="pcCard">
                    <div className="pcBody">
                        <div className="pcTopRow">
                            <div>
                                <h2 className="pcTitle">Natures de mission</h2>
                                <div className="pcMuted" style={{ fontSize: 12, display: "flex", gap: 6, alignItems: "center" }}>
                                    {/*<Info size={14} /> Exemples: evenement, formation, reunion client.*/}
                                </div>
                            </div>
                            <button className="pcBtn pcBtn--primary" type="button" onClick={() => openNatureModal()} disabled={natureLoading}>
                                <Plus size={16} /> Ajouter une nature de mission
                            </button>
                        </div>

                        <div className="pcTableWrap">
                            <table className="pcTable">
                                <thead>
                                <tr>
                                    <th>Code</th>
                                    <th>Libelle</th>
                                    <th>Description</th>
                                    <th>Statut</th>
                                    <th className="is-right">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {natureLoading ? (
                                    <tr><td colSpan={5} className="pcEmptyCell">Chargement...</td></tr>
                                ) : natureMissions.length === 0 ? (
                                    <tr><td colSpan={5} className="pcEmptyCell">Aucune nature de mission configuree</td></tr>
                                ) : natureMissions.map((nature) => (
                                    <tr key={nature.id}>
                                        <td><span className="pcCode" style={{ backgroundColor: "#204385" }}>{nature.code}</span></td>
                                        <td><div className="pcStrong">{nature.libelle}</div></td>
                                        <td><div className="pcMuted" style={{ fontSize: 12 }}>{nature.description || "-"}</div></td>
                                        <td>
                                            <button className={`pcPill ${nature.actif ? "is-on" : "is-off"}`} type="button" onClick={() => toggleNatureActive(nature)} disabled={natureLoading}>
                                                {nature.actif ? "Actif" : "Inactif"}
                                            </button>
                                        </td>
                                        <td className="is-right">
                                            <div className="pcRowActions">
                                                <button className="pcIconBtn" type="button" title="Modifier" onClick={() => openNatureModal(nature)} disabled={natureLoading}>
                                                    <Edit size={16} />
                                                </button>
                                                <button className="pcIconBtn pcIconBtn--danger" type="button" title="Supprimer" onClick={() => handleNatureDelete(nature)} disabled={natureLoading}>
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {renderNatureModal()}
        </div>
    );
};

export default ParamMissions;
